/** DOCUMENTUL CU VARIANTA DE RECOMPARTIMENTARE PUSĂ ÎN LOC, pentru numele camerelor.
 *
 *  Numele și cotele unui nivel de arhitectură le scrie `AdnotariNivel`, din document —
 *  nu `StoreyView`. Previzualizarea desenată doar peste plan lăsa deci camerele noi fără
 *  nume (sau cu numele celor vechi peste ele). Aici documentul primește camerele
 *  variantei în locul celor vechi ale apartamentului, iar pereții și ușile vechi din
 *  interiorul lui ies: numele se scriu cu ACELAȘI cod ca pe planul scris, iar cotele
 *  pereților vechi nu mai rămân peste camerele noi.
 *
 *  Totul în cadrul desenului (`laOrigineLocala` după `northUp`), ca `PrevizRecomp`.
 */
import type { CanonicalDoc, CanonicalObject } from "../canonical";
import type { PrevizRecomp } from "./StoreyView";
import { pointInPolygon } from "../rules";

type Pt = [number, number];
const BETON = new Set(["structural", "facade", "fire", "party"]);

function centrul(poly: number[][]): Pt {
  let x = 0, y = 0;
  for (const p of poly) { x += p[0]; y += p[1]; }
  return [x / poly.length, y / poly.length];
}

/** Punctul cade în amprenta variantei (o cameră sau un perete nou)? */
function inAmprenta(p: Pt, amprente: number[][][]): boolean {
  return amprente.some((r) => r.length >= 3 && pointInPolygon(p[0], p[1], r as Pt[]));
}

function eBeton(o: CanonicalObject): boolean {
  const aw = (o.props?.["architecture.wall"] ?? {}) as { material?: string };
  return aw.material === "beton" || BETON.has(String(o.properties.kind ?? ""));
}

export function docCuVariante(doc: CanonicalDoc, previz: readonly PrevizRecomp[]): CanonicalDoc {
  if (!previz.length) return doc;
  const peFlat = new Map(previz.map((p) => [p.eid, p]));
  const etajul = new Map<string, string>();
  for (const o of doc.objects ?? []) {
    if (o.type === "Flat" && peFlat.has(o.id)) etajul.set(o.id, String(o.properties.storey ?? ""));
  }
  if (!etajul.size) return doc;
  const amprente = new Map<string, number[][][]>();          // pe nivel
  for (const [eid, st] of etajul) {
    const p = peFlat.get(eid)!;
    amprente.set(st, [...(amprente.get(st) ?? []), ...p.camere.map((c) => c.poly), ...p.pereti]);
  }
  const vechi = previz.flatMap((p) => (p.intrareVeche ? [p.intrareVeche] : []));
  const amprentaLui = (o: CanonicalObject) => amprente.get(String(o.properties?.storey ?? ""));
  // PEREȚII VECHI DIN INTERIOR: zidăria al cărei mijloc cade în amprenta variantei
  // (betonul rămâne — e al structurii); întâi ei, ca ușile lor să plece cu ei
  const scoase = new Set<string>();
  for (const o of doc.objects ?? []) {
    const a = o.type === "Wall" ? amprentaLui(o) : undefined;
    const poly = o.geometry?.poly as number[][] | undefined;
    if (a && poly && poly.length >= 3 && !eBeton(o) && inAmprenta(centrul(poly), a)) scoase.add(o.id);
  }
  const fara = (doc.objects ?? []).filter((o) => {
    const a = amprentaLui(o);
    if (!a) return true;
    if (o.type === "Wall") return !scoase.has(o.id);
    if (o.type === "Space") {
      // camerele vechi ale apartamentului, nu și balcoanele / terasele lui (stau afară)
      const f = o.properties.flat as string | undefined;
      const poly = o.geometry?.poly as number[][] | undefined;
      if (!f || !peFlat.has(f) || !poly || poly.length < 3) return true;
      return !inAmprenta(centrul(poly), a);
    }
    if (o.type === "Door") {
      if (scoase.has(String(o.properties.wall ?? ""))) return false;
      const cx = o.geometry?.cx, cy = o.geometry?.cy;
      if (typeof cx !== "number" || typeof cy !== "number") return true;
      // ușa de intrare veche, când varianta o mută
      if (vechi.some((q) => Math.hypot(cx - q[0], cy - q[1]) < 0.35)) return false;
      return !inAmprenta([cx, cy], a);
    }
    return true;
  });
  const noi: CanonicalObject[] = [];
  for (const [eid, st] of etajul) {
    peFlat.get(eid)!.camere.forEach((c, i) => {
      if (c.poly.length < 3) return;
      noi.push({ id: `pv_${eid}_${i}`, type: "Space" as CanonicalObject["type"],
                 properties: { storey: st, flat: eid, kind: c.fel } as CanonicalObject["properties"],
                 geometry: { poly: c.poly } });
    });
  }
  return { ...doc, objects: [...fara, ...noi] };
}
